"use client";

import { useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GetStartedCallToAction() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const buttonRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!buttonRef.current) return;
    const button = buttonRef.current;
    let animationFrameId: number;
    const animateShimmer = () => {
      button.style.setProperty("--shimmer-progress", "0%");
      const startTime = Date.now();
      const duration = 1500;
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        button.style.setProperty("--shimmer-progress", `${progress * 100}%`);
        if (progress < 1) {
          animationFrameId = requestAnimationFrame(animate);
        }
      };
      animationFrameId = requestAnimationFrame(animate);
    };
    const interval = setInterval(animateShimmer, 5000);
    animateShimmer();
    return () => {
      clearInterval(interval);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);
  return <section id="get-started-calltoaction" className="dark bg-background text-foreground py-24 px-4 sm:py-32 md:py-40" ref={ref}>
      <div className={`max-w-3xl mx-auto text-center transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-semibold tracking-tight mb-6 leading-tight">
          Ready to Streamline Your Administrative Operations?
        </h2>
        
        <p className="text-lg sm:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
          Let AJ Dynasty Smart Behavioral Solution handle your billing, credentialing, and administrative support so you can focus on patient care.
        </p>

        <div ref={buttonRef} className="inline-block relative overflow-hidden rounded-[var(--radius-md)]" style={{
        background: "linear-gradient(90deg, transparent 0%, transparent calc(var(--shimmer-progress, 0%) - 15%), rgba(255, 255, 255, 0.3) var(--shimmer-progress, 0%), transparent calc(var(--shimmer-progress, 0%) + 15%), transparent 100%), hsl(var(--color-primary))"
      }}>
          <Link to="/GetStartedPage">
            <Button size="lg" className="bg-transparent hover:bg-transparent text-foreground-primary font-semibold px-8 py-6 text-lg shadow-[var(--shadow-medium)] hover:shadow-[var(--shadow-strong)] transition-all duration-[var(--duration-fast)] hover:-translate-y-1 hover:brightness-95 active:translate-y-0 active:brightness-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              Get Started Today
              <ArrowRight className="ml-2 size-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}